// GENERATED — DO NOT HAND-EDIT.
//
// Source of truth: specs/tier-05-experience/donor.entity-hint.yaml
// Generator: tools/v2-gen/v2gen.py
// Regenerate via: make v2-generate-screens
//
// Hand-edits to non-override-block content WILL be lost on next
// regeneration. See specs/_product/dashboard-v2-page-generator.md §5
// for override blocks (imports / helpers / header-actions).

import type { ListPageConfig } from "../../components/composition/ListPage";
import { donorsApi } from "../../api/donors";
import type { Donor } from "../../api/types";
// override-block:imports BEGIN
// CHG-1294 / ITER-3419 — per-page RBAC gate wiring (consumes GATE_CATALOG).
// Header-action ids match GATE_CATALOG.donor_list keys; toGate adapts
// the catalog {permission} shape to the primitive {require} shape.
// PageHeader's CHG-1291 GatedActionButton picks up permissionGate
// automatically.
import { GATE_CATALOG } from "../../auth/gateCatalog";
import { toGate } from "../../auth/toGateProp";
// CHG-1330 — bulk-tag UX. The imperative opener routes the selected
// row ids to BulkTagDialogHost (mounted in App.tsx).
import { openBulkTagDialog } from "../../lib/bulk-tag-dialog";
// CHG-1666 / ITER-3778 — bulk-export CSV wiring. downloadCsv hits the
// canonical /donors/export?format=csv endpoint; per-ids slicing is
// forward-promised on the BE (today the export is org-wide). When the
// BE adds an `ids=` filter, swap the path to include the joined ids
// without touching the override-block contract.
import { downloadCsv } from "../../lib/download-file";
// override-block:imports END

// override-block:helpers BEGIN
// override-block:helpers END

export const donorListConfig: ListPageConfig<Donor> = {
  title: "Donors",
  subtitle: "Everyone who's given, pledged, or expressed interest.",
  queryKey: ["donors", "list"] as const,
  queryFn: ({ page, pageSize, sortField, sortDirection, search, filters }) =>
    donorsApi.listDonors({ page, pageSize, sortField, sortDirection, search, filters }),
  defaultSort: { field: "last_gift_at", direction: "desc" },
  pageSize: 50,
  rowHref: "/donors/{id}",
  search: { enabled: true, placeholder: "Search donors by name or email…" },
  ambientRow: {
    stageField: "lifecycle_stage",
    entity: "donor",
    activeStages: ["at_risk", "lapsed"],
  },
  emptyState: {
    title: "No donors yet",
    subtitle: "Donors appear here after their first gift, pledge, or import.",
  },
  headerActions: [
    // override-block:header-actions BEGIN
    {
      id: "record-donation",
      label: "Record donation",
      variant: "primary",
      permissionGate: toGate(GATE_CATALOG.donor_list["record-donation"], "hide"),
    },
    {
      id: "record-pledge",
      label: "Record pledge",
      variant: "secondary",
      permissionGate: toGate(GATE_CATALOG.donor_list["record-pledge"], "hide"),
    },
    {
      id: "export-history",
      label: "Export",
      variant: "ghost",
      permissionGate: toGate(GATE_CATALOG.donor_list["export-history"], "disable"),
    },
    // override-block:header-actions END
  ],
  bulkActions: [
    {
      id: "tag",
      label: "Tag selected",
      variant: "primary",
      permission: "alora-giving.tag.attach",
      permissionFallback: "hide",
      onAction:
        // override-block:bulk-action-tag BEGIN
        (ids) => openBulkTagDialog({ entityType: "donor", ids }),
        // override-block:bulk-action-tag END
    },
    {
      id: "export-csv",
      label: "Export CSV",
      variant: "secondary",
      permission: "alora-giving.donor.export",
      permissionFallback: "disable",
      onAction:
        // override-block:bulk-action-export-csv BEGIN
        (_ids) => downloadCsv("/donors/export?format=csv", "donors.csv"),
        // override-block:bulk-action-export-csv END
    },
  ],
  filterChips: [
    { id: "lifecycle-active", label: "Active", field: "lifecycle_stage", value: "active", group: "lifecycle" },
    { id: "lifecycle-at-risk", label: "At risk", field: "lifecycle_stage", value: "at_risk", group: "lifecycle" },
    { id: "lifecycle-failed", label: "Failed", field: "lifecycle_stage", value: "failed", group: "lifecycle" },
    { id: "lifecycle-lapsed", label: "Lapsed", field: "lifecycle_stage", value: "lapsed", group: "lifecycle" },
    { id: "activity-30d", label: "Active 30d", field: "days_since_last_gift", value: 30, group: "recency" },
    { id: "activity-90d", label: "Active 90d", field: "days_since_last_gift", value: 90, group: "recency" },
    { id: "activity-365d", label: "Active 365d", field: "days_since_last_gift", value: 365, group: "recency" },
    { id: "has-recurring", label: "Has recurring", field: "has_recurring", value: true },
  ],
  columns: [
    { field: "full_name", label: "Name", type: "avatar-name", sortable: true },
    { field: "email", label: "Email", type: "email" },
    { field: "lifetime_giving_minor_units", label: "Lifetime", type: "currency", currencyField: "organization_default_currency", sortable: true },
    { field: "last_gift_at", label: "Last gift", type: "datetime", sortable: true },
    { field: "lifecycle_stage", label: "Stage", type: "lifecycle-badge" },
    { field: "tags", label: "Tags", type: "chip-list" },
  ],
};
